import { type MutableRefObject, useEffect, useMemo, useRef, useState } from "react";

import {
  type SignupProvisioningContent,
  getSignupProvisioningStatus,
  isSignupProvisioningReady,
  retrySignupProvisioning
} from "@/api/signup/provisioning";
import { PATH } from "@/configs/router/routerConfig";
import { hasPendingWorkspaceSelection as hasPendingWorkspaceSelectionFromSessionUser } from "@/lib/auth/sessionUser";
import { logger } from "@/lib/logger";
import { buildSignupSetupPath, normalizeSignupBackTo } from "@/lib/signup/setupRouting";
import { useSelector } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { useRouter } from "next/router";

const POLLING_STATUS = new Set(["pending", "running", "failed_retryable"]);

const normalizeStatus = (value?: string | null): string => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const buildSignupProvisioningContextKey = (tenantGuid: string | null, userGuid: string | null): string => {
  return `${String(tenantGuid || "").trim()}:${String(userGuid || "").trim()}`;
};

export const isSignupSetupComplete = (
  user?: {
    tenant_config?: { personalization_overlay_completed?: boolean } | null;
    tenantConfig?: { personalization_overlay_completed?: boolean } | null;
  } | null
): boolean => {
  const tenantConfig = user?.tenant_config || user?.tenantConfig;
  return Boolean(
    tenantConfig && typeof tenantConfig === "object" && tenantConfig.personalization_overlay_completed === true
  );
};

export const hasPendingWorkspaceSelection = hasPendingWorkspaceSelectionFromSessionUser;

export const hasSignupProvisioningRun = (content?: SignupProvisioningContent | null): boolean => {
  return Boolean(String(content?.run_id || "").trim());
};

export const isSignupProvisioningAccessReady = (content?: SignupProvisioningContent | null): boolean => {
  if (!content) {
    return false;
  }

  if (typeof content.access_ready === "boolean") {
    return content.access_ready;
  }

  return isSignupProvisioningReady(content);
};

export const shouldRedirectSignupToSetup = (content?: SignupProvisioningContent | null): boolean => {
  return Boolean(content) && !isSignupProvisioningAccessReady(content);
};

const isSignupProvisioningPollable = (content?: SignupProvisioningContent | null): boolean => {
  if (!content) {
    return false;
  }

  if (isSignupProvisioningReady(content)) {
    return false;
  }

  return POLLING_STATUS.has(normalizeStatus(content.status));
};

const isPageVisible = (): boolean => {
  if (typeof document === "undefined") {
    return true;
  }

  if (typeof document.hidden === "boolean") {
    return !document.hidden;
  }

  if (typeof document.visibilityState === "string") {
    return document.visibilityState !== "hidden";
  }

  return true;
};

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const maybeError = error as { message?: string; localizeMessage?: string; status?: number };
    return (
      maybeError.localizeMessage ||
      maybeError.message ||
      (maybeError.status ? `status_${maybeError.status}` : "request_failed")
    );
  }

  return "request_failed";
};

const shouldRetrySignupProvisioningStatusError = (error: unknown): boolean => {
  const status = typeof error === "object" && error ? Number((error as { status?: unknown }).status) : NaN;

  if (status === 401 || status === 404) {
    return false;
  }

  if (!Number.isFinite(status)) {
    return true;
  }

  return status === 0 || status >= 500;
};

function useSignupProvisioningVisibilityRefresh(params: {
  enabled: boolean;
  userGuid: string | null;
  mountedRef: MutableRefObject<boolean>;
  refreshRef: MutableRefObject<(force?: boolean) => Promise<SignupProvisioningContent | null>>;
  clearPollTimer: () => void;
}) {
  const { enabled, userGuid, mountedRef, refreshRef, clearPollTimer } = params;

  useEffect(() => {
    if (!enabled || !userGuid || typeof document === "undefined") {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (!mountedRef.current) {
        return;
      }

      if (!isPageVisible()) {
        clearPollTimer();
        return;
      }

      void refreshRef.current(true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [clearPollTimer, enabled, mountedRef, refreshRef, userGuid]);
}

function useSignupProvisioningLifecycle(params: {
  enabled: boolean;
  contextKey: string;
  tenantGuid: string | null;
  userGuid: string | null;
  content: SignupProvisioningContent | null;
  activeContextKeyRef: MutableRefObject<string>;
  mountedRef: MutableRefObject<boolean>;
  contentRef: MutableRefObject<SignupProvisioningContent | null>;
  inFlightRefreshRef: MutableRefObject<Promise<SignupProvisioningContent | null> | null>;
  refreshAfterInFlightRef: MutableRefObject<boolean>;
  refreshRef: MutableRefObject<(force?: boolean) => Promise<SignupProvisioningContent | null>>;
  clearPollTimer: () => void;
  setContent: (value: SignupProvisioningContent | null) => void;
  setError: (value: string | null) => void;
  setLoading: (value: boolean) => void;
  setRefreshing: (value: boolean) => void;
  setRetrying: (value: boolean) => void;
}) {
  const {
    enabled,
    contextKey,
    tenantGuid,
    userGuid,
    content,
    activeContextKeyRef,
    mountedRef,
    contentRef,
    inFlightRefreshRef,
    refreshAfterInFlightRef,
    refreshRef,
    clearPollTimer,
    setContent,
    setError,
    setLoading,
    setRefreshing,
    setRetrying
  } = params;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPollTimer();
      inFlightRefreshRef.current = null;
      refreshAfterInFlightRef.current = false;
    };
  }, [clearPollTimer, inFlightRefreshRef, mountedRef, refreshAfterInFlightRef]);

  useEffect(() => {
    contentRef.current = content;
  }, [content, contentRef]);

  useEffect(() => {
    if (activeContextKeyRef.current !== contextKey) {
      activeContextKeyRef.current = contextKey;
      setContent(null);
      contentRef.current = null;
      setError(null);
      setLoading(true);
      setRefreshing(false);
      setRetrying(false);
      clearPollTimer();
      inFlightRefreshRef.current = null;
      refreshAfterInFlightRef.current = false;
    }

    if (!enabled || !userGuid || !tenantGuid) {
      activeContextKeyRef.current = contextKey;
      setContent(null);
      contentRef.current = null;
      setError(null);
      setLoading(false);
      setRefreshing(false);
      setRetrying(false);
      clearPollTimer();
      inFlightRefreshRef.current = null;
      refreshAfterInFlightRef.current = false;
      return;
    }

    void refreshRef.current(true);
    return () => {
      clearPollTimer();
    };
  }, [
    contentRef,
    enabled,
    inFlightRefreshRef,
    refreshAfterInFlightRef,
    refreshRef,
    clearPollTimer,
    contextKey,
    setContent,
    setError,
    setLoading,
    setRefreshing,
    setRetrying,
    tenantGuid,
    userGuid
  ]);

  useSignupProvisioningVisibilityRefresh({ enabled, userGuid, mountedRef, refreshRef, clearPollTimer });
}

function createSignupProvisioningRefreshManager(params: {
  enabled: boolean;
  userGuid: string | null;
  tenantGuid: string | null;
  pollIntervalMs: number;
  contentRef: MutableRefObject<SignupProvisioningContent | null>;
  mountedRef: MutableRefObject<boolean>;
  inFlightRefreshRef: MutableRefObject<Promise<SignupProvisioningContent | null> | null>;
  refreshAfterInFlightRef: MutableRefObject<boolean>;
  pollTimerRef: MutableRefObject<number | null>;
  refreshRef: MutableRefObject<(force?: boolean) => Promise<SignupProvisioningContent | null>>;
  setContent: (value: SignupProvisioningContent | null) => void;
  setError: (value: string | null) => void;
  setLoading: (value: boolean) => void;
  setRefreshing: (value: boolean) => void;
}) {
  const {
    enabled,
    userGuid,
    tenantGuid,
    pollIntervalMs,
    contentRef,
    mountedRef,
    inFlightRefreshRef,
    refreshAfterInFlightRef,
    pollTimerRef,
    refreshRef,
    setContent,
    setError,
    setLoading,
    setRefreshing
  } = params;

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const scheduleNextPoll = (nextContent: SignupProvisioningContent | null) => {
    clearPollTimer();
    if (
      !enabled ||
      !mountedRef.current ||
      !nextContent ||
      !isSignupProvisioningPollable(nextContent) ||
      !isPageVisible()
    ) {
      return;
    }

    pollTimerRef.current = window.setTimeout(() => {
      pollTimerRef.current = null;
      void refreshRef.current(true);
    }, pollIntervalMs);
  };

  const scheduleRetryAfterFailure = () => {
    clearPollTimer();
    if (!enabled || !mountedRef.current || !userGuid || !isPageVisible()) {
      return;
    }

    pollTimerRef.current = window.setTimeout(() => {
      pollTimerRef.current = null;
      void refreshRef.current(true);
    }, pollIntervalMs);
  };

  const refresh = async (force = false): Promise<SignupProvisioningContent | null> => {
    if (!enabled || !userGuid || !tenantGuid) {
      return null;
    }

    clearPollTimer();

    if (inFlightRefreshRef.current) {
      if (force) {
        refreshAfterInFlightRef.current = true;
      }
      return inFlightRefreshRef.current;
    }

    const hasContent = Boolean(contentRef.current);
    if (!hasContent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    const request = getSignupProvisioningStatus({ force, tenantGuid, userGuid, suppressAuthLogoutOn401: true })
      .then((response) => {
        const nextContent = response?.content ?? null;
        if (!mountedRef.current) {
          return nextContent;
        }

        contentRef.current = nextContent;
        setContent(nextContent);
        setError(null);
        scheduleNextPoll(nextContent);
        return nextContent;
      })
      .catch((err) => {
        logger.warn("useSignupProvisioningStatus: fetch failed", { err });
        if (mountedRef.current) {
          setError(extractErrorMessage(err));
          if (shouldRetrySignupProvisioningStatusError(err)) {
            scheduleRetryAfterFailure();
          }
        }
        return null;
      })
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }

        inFlightRefreshRef.current = null;
        if (refreshAfterInFlightRef.current) {
          refreshAfterInFlightRef.current = false;
          if (mountedRef.current && enabled && userGuid && isPageVisible()) {
            void refreshRef.current(true);
          }
        }
      });

    inFlightRefreshRef.current = request;
    return request;
  };

  return { clearPollTimer, refresh };
}

function buildSignupProvisioningRetry(params: {
  enabled: boolean;
  userGuid: string | null;
  tenantGuid: string | null;
  refresh: (force?: boolean) => Promise<SignupProvisioningContent | null>;
  refreshAfterInFlightRef: MutableRefObject<boolean>;
  mountedRef: MutableRefObject<boolean>;
  setRetrying: (value: boolean) => void;
  setError: (value: string | null) => void;
}) {
  const { enabled, userGuid, tenantGuid, refresh, refreshAfterInFlightRef, mountedRef, setRetrying, setError } = params;

  return async (): Promise<SignupProvisioningContent | null> => {
    if (!enabled || !userGuid || !tenantGuid) {
      return null;
    }

    setRetrying(true);
    try {
      await retrySignupProvisioning({ tenantGuid, userGuid, suppressAuthLogoutOn401: true });
      refreshAfterInFlightRef.current = false;
      return await refresh(true);
    } catch (err) {
      logger.warn("useSignupProvisioningStatus: retry failed", { err });
      if (mountedRef.current) {
        setError(extractErrorMessage(err));
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setRetrying(false);
      }
    }
  };
}

export interface UseSignupProvisioningStatusResult {
  content: SignupProvisioningContent | null;
  loading: boolean;
  refreshing: boolean;
  retrying: boolean;
  error: string | null;
  refresh: (force?: boolean) => Promise<SignupProvisioningContent | null>;
  retry: () => Promise<SignupProvisioningContent | null>;
}

export function useSignupProvisioningStatus(options?: {
  enabled?: boolean;
  pollIntervalMs?: number;
}): UseSignupProvisioningStatusResult {
  const enabled = options?.enabled ?? true;
  const pollIntervalMs = options?.pollIntervalMs ?? 4000;
  const user = useSelector((state) => state[rootReducerName.auth].user);
  const tenantGuid = String(user?.tenantGuid || "").trim() || null;
  const userGuid = String(user?.id || "").trim() || null;
  const effectiveEnabled = enabled && Boolean(userGuid) && Boolean(tenantGuid);
  const contextKey = buildSignupProvisioningContextKey(tenantGuid, userGuid);
  const [content, setContent] = useState<SignupProvisioningContent | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const contentRef = useRef<SignupProvisioningContent | null>(null);
  const inFlightRefreshRef = useRef<Promise<SignupProvisioningContent | null> | null>(null);
  const refreshAfterInFlightRef = useRef(false);
  const activeContextKeyRef = useRef(contextKey);
  const mountedRef = useRef(true);
  const refreshRef = useRef<(force?: boolean) => Promise<SignupProvisioningContent | null>>(async () => null);
  const { clearPollTimer, refresh } = useMemo(
    () =>
      createSignupProvisioningRefreshManager({
        enabled: effectiveEnabled,
        userGuid,
        tenantGuid,
        pollIntervalMs,
        contentRef,
        mountedRef,
        inFlightRefreshRef,
        refreshAfterInFlightRef,
        pollTimerRef,
        refreshRef,
        setContent,
        setError,
        setLoading,
        setRefreshing
      }),
    [
      contentRef,
      effectiveEnabled,
      inFlightRefreshRef,
      mountedRef,
      pollIntervalMs,
      pollTimerRef,
      refreshAfterInFlightRef,
      refreshRef,
      setContent,
      setError,
      setLoading,
      setRefreshing,
      tenantGuid,
      userGuid
    ]
  );

  refreshRef.current = refresh;
  const retry = buildSignupProvisioningRetry({
    enabled: effectiveEnabled,
    userGuid,
    tenantGuid,
    refresh,
    refreshAfterInFlightRef,
    mountedRef,
    setRetrying,
    setError
  });

  useSignupProvisioningLifecycle({
    enabled: effectiveEnabled,
    contextKey,
    tenantGuid,
    userGuid,
    content,
    activeContextKeyRef,
    mountedRef,
    contentRef,
    inFlightRefreshRef,
    refreshAfterInFlightRef,
    refreshRef,
    clearPollTimer,
    setContent,
    setError,
    setLoading,
    setRefreshing,
    setRetrying
  });

  return {
    content,
    loading,
    refreshing,
    retrying,
    error,
    refresh,
    retry
  };
}

export interface UseSignupProvisioningGateResult {
  checking: boolean;
  requiresSetup: boolean;
  content: SignupProvisioningContent | null;
}

export function useSignupProvisioningGate(): UseSignupProvisioningGateResult {
  const router = useRouter();
  const user = useSelector((state) => state[rootReducerName.auth].user);
  const tenantGuid = String(user?.tenantGuid || "").trim() || null;
  const userGuid = String(user?.id || "").trim() || null;
  const pendingWorkspaceSelection = hasPendingWorkspaceSelection(user);
  // Start blocked so dashboard content does not flash before the provisioning check resolves.
  const [checking, setChecking] = useState(true);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [content, setContent] = useState<SignupProvisioningContent | null>(null);
  const redirectIssuedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const backTo = normalizeSignupBackTo(router.asPath) || PATH.HOME_PAGE;
    redirectIssuedRef.current = false;

    if (pendingWorkspaceSelection) {
      setChecking(false);
      setRequiresSetup(true);
      setContent(null);
      if (router.isReady && !redirectIssuedRef.current) {
        redirectIssuedRef.current = true;
        void router.replace(buildSignupSetupPath(backTo));
      }
      return () => {
        cancelled = true;
      };
    }

    if (!userGuid) {
      setChecking(false);
      setRequiresSetup(false);
      setContent(null);
      redirectIssuedRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    if (!router.isReady) {
      setChecking(true);
      setRequiresSetup(false);
      setContent(null);
      return () => {
        cancelled = true;
      };
    }

    const fallbackRequiresSetup = !isSignupSetupComplete(user);
    if (!tenantGuid) {
      setChecking(false);
      setRequiresSetup(fallbackRequiresSetup);
      setContent(null);
      if (fallbackRequiresSetup && !redirectIssuedRef.current) {
        redirectIssuedRef.current = true;
        void router.replace(buildSignupSetupPath(backTo));
      }
      return () => {
        cancelled = true;
      };
    }

    setChecking(true);
    void getSignupProvisioningStatus({ tenantGuid, userGuid, suppressAuthLogoutOn401: true })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const nextContent = response?.content ?? null;
        setContent(nextContent);
        const nextRequiresSetup = shouldRedirectSignupToSetup(nextContent);
        setRequiresSetup(nextRequiresSetup);
        setChecking(false);

        if (nextRequiresSetup && !redirectIssuedRef.current) {
          redirectIssuedRef.current = true;
          void router.replace(buildSignupSetupPath(backTo));
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }

        const status = typeof err === "object" && err ? Number((err as { status?: unknown }).status) : NaN;
        if (status === 401) {
          logger.debug("useSignupProvisioningGate: auth error while checking status", { err });
        } else {
          logger.warn("useSignupProvisioningGate: status check failed", { err, fallbackRequiresSetup });
        }

        setContent(null);
        setRequiresSetup(fallbackRequiresSetup);
        setChecking(false);

        if (fallbackRequiresSetup && !redirectIssuedRef.current) {
          redirectIssuedRef.current = true;
          void router.replace(buildSignupSetupPath(backTo));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pendingWorkspaceSelection, router, router.asPath, router.isReady, tenantGuid, userGuid]);

  return {
    checking,
    requiresSetup,
    content
  };
}
